import React, { useCallback, useEffect, useState } from 'react';
import styles from './AllFonts.module.scss';
import VideoService from '../../../../../../../service/VideoService';
import Spinner from '../../../../../../../../common/components/Spinner';
import { CustomButton } from '../../../../../../../components/Button/Button';
import Icon from '../../../../../../../components/Icon/Icon';
import { useTranslation } from 'react-i18next';
import SearchComponent from '../../../../../../../components/SearchComponent/SearchComponent';
import usePagination from '../../../../../../../hooks/usePagination';
import Pagination from '../../../../../../../components/Pagination/Pagination';
import UploadFontModal from '../../../UploadFontModal/UploadFontModal';
import EditFontModal from '../../../EditFontModal/EditFontModal';
import RadioButton from '../../../../../../../components/RadioButton/RadioButton';
import { useUserContext } from '../../../../../../../context/UserContext';
import { ToastNotification } from '../../../../../../../components/Toast/Toast';
import { getSortIcon } from '../../utils';
import * as Sentry from '@sentry/browser';
import cx from 'classnames';
import { toast } from 'react-toastify';

const FONT_ITEM_SIZE = 16;

const AllFonts = ({ }) => {
  const [fontToEdit, setFontToEdit] = useState(null);
  const [uploadFontModal, setUploadFontModal] = useState(false);
  const [editFontModal, setEditFontModal] = useState(false);
  const [allCompanies, setAllCompanies] = useState([]);
  const [savingChanges, setSavingChanges] = useState([]);
  const [fontLoading, setFontLoading] = useState(null);

  const { updateUserAppConfig } = useUserContext();

  const { t } = useTranslation();

  const handleSort = field => {
    onSort({ sortBy: field, sortOrder: query.sortOrder === 'asc' ? 'desc' : 'asc' });
    return;
  };

  const handleOpenFontModal = async id => {
    setEditFontModal(true);
    if (!allCompanies?.length) {
      const companies = await VideoService.getCompaniesList();
      setAllCompanies(companies);
    }
    const fontById = await VideoService.getFontById(id);
    setFontToEdit(fontById);
  };

  const handleUploadFont = () => {
    updateUserAppConfig();

    loadList(true);
  };

  const handleCloseFontModal = () => {
    setEditFontModal(false);
    setTimeout(() => {
      setFontToEdit(null);
    }, 250);
  };

  const getFontsList = useCallback(async params => {
    try {
      const fontsList = await VideoService.getFontsListForManagement(params);
      return fontsList;
    } catch (e) {
      console.log('e', e);
    }
  }, []);

  const { list: fontsList, query, totalData, isLoading: loading, onSearch, onChangePage, onSort, loadList } = usePagination({
    service: getFontsList,
    sortByFiled: 'name',
  });

  const handleUpdateFont = async (id, isDefault = null, companies = null, newName = null) => {
    setSavingChanges(prevState => [...prevState, id]);
    const payload = {};
    if (isDefault !== null) {
      payload.default = isDefault;
    }
    if (companies !== null) {
      payload.companies = companies;
    }
    if (newName) {
      payload.name = newName;
    }
    await VideoService.updateFont(id, payload);

    updateUserAppConfig();

    loadList(true);

    if (newName && isDefault === null && companies === null) {
      const font = fontsList.find(item => item.id === id);
      ToastNotification({
        message: `${t('company.settings.fonts.successfully_renamed', { oldName: font.name, newName })}`,
        iconName: 'success',
      });
    } else {
      ToastNotification({
        message: `${t('company.settings.fonts.successfully_updated')}`,
        iconName: 'success',
      });
    }

    setTimeout(() => {
      setSavingChanges(prevState => prevState.filter(item => item !== id));
    }, 400);
  };

  const handleDeleteFont = async (e, id, name) => {
    e.stopPropagation();
    const isConfirm = confirm(t('company.settings.fonts.manage.remove_confirm', { fontName: name }));
    if (isConfirm) {
      const loadingToast = ToastNotification({
        message: `${t('general.toast.deleting')}`,
        spinner: true,
        autoClose: false,
      });
      try {
        setFontLoading(id);
        await VideoService.deleteFont(id);
        updateUserAppConfig();
        toast.dismiss(loadingToast);
        loadList(true);
      } catch (e) {
        toast.dismiss(loadingToast);
        Sentry.captureException(`Error removing font ${name}: ${e}`);
        ToastNotification({
          message: t('company.settings.fonts.manage.remove_error', { fontName: name }),
        });
      } finally {
        setFontLoading(null);
      }
    }
  };

  const checkIfFontLoadedOrLoad = async fonts => {
    await Promise.all(
      fonts.map(async fontItem => {
        try {
          //If we specify a font that is not in the FontFaceSet and is not a system font, check() returns true, because in this situation we will not rely on any fonts from the set:
          if (document.fonts.check(`${FONT_ITEM_SIZE}px '${fontItem?.name}'`)) {
            var labelFont = new FontFace(fontItem?.name, `url(${fontItem?.url})`);
            const loadedFace = await labelFont.load();
            document.fonts.add(loadedFace);
          }
        } catch (e) {
          console.log('Font loaded failed with error:', e);
        }
      })
    );
  };

  useEffect(() => {
    checkIfFontLoadedOrLoad(fontsList);
  }, [fontsList]);

  return (
    <div className={styles.wrapper}>
      <div>
        <div className={styles.headerContainer}>
          <h2 className={styles.header}>{t('company.settings.fonts.all_title', { fontsAmount: totalData })}</h2>
          <SearchComponent searchValue={query.search} onChange={onSearch} clearButton />
        </div>
        <div className={styles.fontsTable}>
          <div className={styles.fontsTableHeader}>
            <div className={styles.itemHeader} onClick={() => handleSort('name')}>
              <span>{t('company.settings.users.name')}</span>
              <Icon src={getSortIcon('name', query)} verticalAlign="middle" className={styles.sortIcon} />
            </div>
            <div className={styles.defaultHeader}>{t('company.settings.fonts.default')}</div>
          </div>
          {loading ? (
            <div className={styles.spinnerWrapper}>
              <Spinner size="3px" width="40px" height="40px" />
            </div>
          ) : (
            <ul className={styles.fontList}>
              {fontsList.map(({ id, name, default: isDefault }) => (
                <li key={id} className={cx(styles.fontItem, { [styles.loading]: fontLoading == id })} onClick={() => handleOpenFontModal(id)}>
                  <span className={styles.fontName} style={{ fontFamily: `'${name}'` }}>
                    {name}
                  </span>
                  <div className={styles.actionsContainer}>
                    <RadioButton
                      checked={isDefault}
                      id={id}
                      variant="secondary"
                      onChange={() => handleUpdateFont(id, !isDefault)}
                      className={styles.checkbox}
                      disabled={savingChanges?.find(item => item === id)}
                    />
                    <CustomButton variant="link" className={styles.removeBtn} onClick={e => handleDeleteFont(e, id, name)} ariaLabel={t('accessibility.bin')}>
                      <Icon src="bin_red" className={styles.binIcon} />
                    </CustomButton>
                  </div>
                  {fontLoading == id ? <Spinner width={'30px'} height={'30px'} size={'3px'} className={styles.fontLoadingSpinner} /> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className={styles.footerContainer}>
        <CustomButton variant="primary" label={t('company.settings.fonts.upload_font')} onClick={() => setUploadFontModal(true)} />
        {!!totalData && (
          <Pagination currentPage={query.page} totalPages={Math.ceil(totalData / query.perPage)} changePage={onChangePage} />
        )}
      </div>
      <UploadFontModal isOpen={uploadFontModal} onClose={() => setUploadFontModal(false)} onUpload={handleUploadFont} />
      <EditFontModal
        allCompanies={allCompanies}
        open={editFontModal}
        onClose={handleCloseFontModal}
        fontData={fontToEdit}
        onSave={handleUpdateFont}
      />
    </div>
  );
};

export default AllFonts;
